import { MC_ENTRY_SOURCE } from '@mgCommon/constants/constants';
import { MC_ENTRY_SOURCE_SHORT_NAME_MAPPER } from '@mgCommon/constants/mappings';
import McValidationService from '@mgServices/mc-validation.service';
import type { AppDispatch, CbRawTemplate, RootState, StateStatus } from '@mgStore/types';
import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import to from 'await-to-js';
import { validateDeTable } from './mcValidationSlice';

export type RealParam = {
  name: string;
  value: string;
  proofingValue: string;
  tableSource: 'JOURNEY' | 'DE';
};

type RealParamsState = {
  realValuesList: RealParam[];
  status: StateStatus;
  error: string | null;
};

type GetRealParamValuesArgs = {
  fuel2token?: string | null;
  dataExtensionId?: string | null;
};

const initialState: RealParamsState = {
  realValuesList: [],
  status: 'idle',
  error: null,
};

export const getRealParamValues = createAsyncThunk<
  RealParam[],
  GetRealParamValuesArgs,
  { state: RootState; dispatch: AppDispatch }
>('realParams/getRealParamValues', async ({ fuel2token = null, dataExtensionId = null }, { getState, dispatch }) => {
  let selectedTemplate = getState().templates.selectedTemplate as CbRawTemplate;
  if (selectedTemplate && Object.keys(selectedTemplate).length === 0) {
    selectedTemplate = getState().cbSelectedTemplate as CbRawTemplate;
  }
  let realValuesList: RealParam[] = [];

  await Promise.all(
    selectedTemplate.dynamicVariables?.map(async d => {
      let customerKey = '';
      let paramTableName = '';
      let paramBareValue = '';

      if (d.tableSource === MC_ENTRY_SOURCE.DATA_EXTENSION) {
        [paramTableName = '', paramBareValue = ''] = d.value.split('.') ?? [];
        const [err, result] = await to(dispatch(validateDeTable({ fuel2token, deName: paramTableName })).unwrap());
        customerKey = result?.customerKey || '';

        if (err) {
          console.error('realParams/getRealParamValues ----> getCustomerKey', err.message);
        }
      }

      const key = customerKey || dataExtensionId;
      const res = await McValidationService.getRealParamValues({
        fuel2token,
        key,
        propertyName: paramBareValue || d.value,
        entry: MC_ENTRY_SOURCE_SHORT_NAME_MAPPER[d.tableSource] ?? '',
      });

      const data = res?.data?.result;
      if (data) {
        const random = Math.floor(Math.random() * data.length);
        realValuesList = [
          ...realValuesList,
          { name: d.name, value: data[random], proofingValue: d.proofingValue, tableSource: d.tableSource },
        ];
      }
    })
  );

  return realValuesList;
});

const realParamsSlice = createSlice({
  name: 'realParams',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getRealParamValues.pending, state => {
        state.status = 'loading';
      })
      .addCase(getRealParamValues.fulfilled, (state, action: PayloadAction<RealParam[]>) => {
        state.realValuesList = action.payload;
        state.status = 'succeeded';
      })
      .addCase(getRealParamValues.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || null;
      });
  },
});

export default realParamsSlice.reducer;
